import React, { useMemo } from "react";
import { Drawer } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { Segmented } from "../../../design-system";
import { ReactComponent as ThinkerIcon } from "../../../assets/icons/svg/thinker-gradient.svg";
import { AskChatPanel } from "./AskChatPanel";
import { AskQueuePanel } from "./AskQueuePanel";
import { getScriptForProject } from "./askScript";
import { AssistantTab } from "./types";
import { useAskMachine } from "./useAskMachine";
import classes from "./DrawerAssistant.module.less";

interface Props {
    open: boolean;
    onClose: () => void;
    projectName?: string | null;
}

export const DrawerAssistant: React.FC<Props> = ({ open, onClose, projectName }) => {
    const script = useMemo(() => getScriptForProject(projectName), [projectName]);
    const {
        state,
        runNextExchange,
        queueExchange,
        editQueuedQuestion,
        addQueuedQuestion,
        removeQueuedQuestion,
        attachDoc,
        attachDocFromChat,
        removeQueuedDoc,
        setTab,
        submit,
    } = useAskMachine(script);

    const handleAttachFromChat = () => {
        const docs = script.attachableDocs;
        if (docs.length === 0) return;
        const idx = state.queuedDocs.length % docs.length;
        const doc = docs[idx];
        attachDocFromChat(doc.filename, doc.size);
    };

    const queueCount = state.queuedQuestions.length + state.queuedDocs.length;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={520}
            mask={false}
            closable={false}
            destroyOnClose
            rootClassName={classes.drawerRoot}
            className={classes.drawer}
            bodyStyle={{ padding: 0 }}
        >
            <div className={classes.shell}>
                <div className={classes.header}>
                    <ThinkerIcon className={classes.headerIcon} />
                    <button
                        type="button"
                        className={classes.headerClose}
                        onClick={onClose}
                        title="Close"
                    >
                        <CloseOutlined />
                    </button>
                </div>

                <div className={classes.tabs}>
                    <Segmented
                        className={classes.tabSegmented}
                        value={state.activeTab}
                        onChange={(value) => setTab(value as AssistantTab)}
                        options={[
                            { label: "Chat", value: "chat" },
                            {
                                label: (
                                    <span className={classes.queueLabel}>
                                        Queue
                                        {queueCount > 0 && (
                                            <span className={classes.tabBadge}>
                                                {queueCount}
                                            </span>
                                        )}
                                    </span>
                                ),
                                value: "queue",
                            },
                        ]}
                    />
                </div>

                <div className={classes.tabBody}>
                    {state.activeTab === "chat" ? (
                        <AskChatPanel
                            messages={state.messages}
                            recordingTranscript={state.recordingTranscript}
                            canRunNext={state.cursor < script.exchanges.length}
                            onRunNext={runNextExchange}
                            onQueueExchange={queueExchange}
                            onAttachDoc={handleAttachFromChat}
                        />
                    ) : (
                        <AskQueuePanel
                            queuedQuestions={state.queuedQuestions}
                            queuedDocs={state.queuedDocs}
                            submitted={state.submitted}
                            onEditQuestion={editQueuedQuestion}
                            onAddQuestion={addQueuedQuestion}
                            onRemoveQuestion={removeQueuedQuestion}
                            onAttachDoc={attachDoc}
                            onRemoveDoc={removeQueuedDoc}
                            onSubmit={submit}
                            onClose={onClose}
                        />
                    )}
                </div>
            </div>
        </Drawer>
    );
};
